import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { logout, getUnreadMessages, getMyApplications } from "@/lib/api";
import { Lock,
  LayoutDashboard,
  FileText,
  Mic,
  Brain,
  Briefcase,
  Inbox,
  LogOut,
  Menu,
  X,
  User,
  MessageSquare,
  Gift,
  TrendingUp,
  GraduationCap,
  Sparkles,
  Building2,
} from "lucide-react";
import WishireLogo from "@/components/wishire/WishireLogo";

const NAV_GROUPS = [
  {
    label: "Main",
    items: [
      { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testId: "app-nav-dashboard" },
      { to: "/app/profile", label: "Profile", icon: User, testId: "app-nav-profile" },
    ],
  },
  {
    label: "Get hired",
    items: [
      { to: "/app/resume", label: "Resume", icon: FileText, testId: "app-nav-resume" },
      { to: "/app/interview", label: "Interview", icon: Mic, testId: "app-nav-interview" },
      { to: "/app/coach", label: "Coach", icon: Brain, testId: "app-nav-coach" },
      { to: "/app/companies", label: "Companies", icon: Building2, testId: "app-nav-companies" },
    ],
  },
  {
    label: "Jobs",
    items: [
      { to: "/app/jobs", label: "Jobs", icon: Briefcase, testId: "app-nav-jobs" },
      { to: "/app/wishagent", label: "WishAgent", icon: Sparkles, testId: "app-nav-wishagent" },
      { to: "/app/campus", label: "Campus", icon: GraduationCap, testId: "app-nav-campus" },
      { to: "/app/applications", label: "Applications", icon: Inbox, testId: "app-nav-applications" },
      { to: "/app/messages", label: "Messages", icon: MessageSquare, testId: "app-nav-messages" },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/app/score-insights", label: "Score & Salary", icon: TrendingUp, testId: "app-nav-score" },
      { to: "/app/referrals", label: "Referrals", icon: Gift, testId: "app-nav-referrals" },
    ],
  },
];

export default function AppLayout() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [counts, setCounts] = useState({ "/app/applications": 0, "/app/messages": 0 });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [apps, msgs] = await Promise.allSettled([getMyApplications(), getUnreadMessages()]);
        if (!alive) return;
        const appCount = apps.status === "fulfilled" && Array.isArray(apps.value.data) ? apps.value.data.length : 0;
        const msgCount = msgs.status === "fulfilled" ? (msgs.value.data?.unread ?? 0) : 0;
        setCounts({ "/app/applications": appCount, "/app/messages": msgCount });
      } catch (_) {}
    })();
    return () => { alive = false; };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (e) {}
    setUser(null);
    navigate("/", { replace: true });
  };

  // Campus students (candidates linked to a college) get a focused nav:
  // hide Referrals / external Jobs / Messages and trim "Score & Salary" → "Score".
  const isCampusStudent = user?.role === "candidate" && !!user?.college_id;
  const HIDE_FOR_CAMPUS = new Set(["/app/referrals", "/app/jobs", "/app/messages"]);
  // WishAgent (auto-apply) is admin-gated: hidden until the flag is on.
  const wishagentOn = !!user?.features?.wishagent;
  const base = wishagentOn
    ? NAV_GROUPS
    : NAV_GROUPS.map((g) => ({ ...g, items: g.items.filter((it) => it.to !== "/app/wishagent") }))
        .filter((g) => g.items.length > 0);
  const navGroups = isCampusStudent
    ? base.map((g) => ({
        ...g,
        items: g.items
          .filter((it) => !HIDE_FOR_CAMPUS.has(it.to))
          .map((it) => (it.to === "/app/score-insights" ? { ...it, label: "Score" } : it)),
      })).filter((g) => g.items.length > 0)
    : base;

  // Licensed-seat lock: the account exists but the college hasn't licensed it.
  if (user?.college_id && user?.license_active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] text-stone-900 p-6">
        <div className="max-w-md w-full rounded-3xl border border-stone-200 bg-white p-10 text-center" data-testid="seat-locked">
          <div className="inline-flex p-4 rounded-2xl bg-amber-50 border border-amber-200 text-[#C2410C] mb-4">
            <Lock size={28} />
          </div>
          <h1 className="font-display text-3xl">Your seat isn't active yet</h1>
          <p className="text-sm text-stone-600 mt-3 leading-relaxed">
            Your account is on Wishire, but your college has used all its licensed seats.
            Ask your placement cell to activate your seat — everything unlocks the moment they do.
          </p>
          <p className="text-xs text-stone-400 mt-4">{user.email}</p>
          <button onClick={() => { logout().finally(() => { setUser(null); window.location.href = "/login"; }); }}
            className="mt-6 text-sm font-medium text-stone-500 hover:text-stone-900 underline">Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[#FDFBF7] text-stone-900">
      {/* Sidebar */}
      <aside
        className={`fixed md:sticky inset-y-0 left-0 z-40 w-64 bg-stone-900 text-stone-100 flex flex-col transform transition-transform md:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        } md:h-screen md:top-0`}
      >
        {/* Logo */}
        <div className="px-5 pt-5 pb-4 shrink-0">
          <button
            onClick={() => navigate("/")}
            className="flex items-baseline gap-1"
            data-testid="app-logo"
          >
            <WishireLogo dark size="md" />
          </button>
        </div>

        {/* Scrollable nav */}
        <nav className="flex-1 overflow-y-auto px-3 pb-2" style={{ scrollbarWidth: "none" }}>
          {navGroups.map((group) => (
            <div key={group.label} className="mb-4">
              <p className="px-3 mb-1 text-[10px] font-mono uppercase tracking-[0.15em] text-stone-500">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    data-testid={n.testId}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 ${
                        isActive
                          ? "bg-gradient-to-r from-[#C2410C]/30 to-[#F59E0B]/10 text-white"
                          : "text-stone-400 hover:bg-white/5 hover:text-stone-100"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {isActive && (
                          <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-gradient-to-b from-[#F59E0B] to-[#C2410C]" />
                        )}
                        <n.icon
                          size={16}
                          className={isActive ? "text-amber-300" : "text-stone-500 group-hover:text-stone-300"}
                        />
                        <span className="text-sm font-medium">{n.label}</span>
                        {counts[n.to] > 0 && (
                          <span
                            data-testid={`nav-badge-${n.testId}`}
                            className="ml-auto min-w-[20px] h-5 px-1.5 inline-flex items-center justify-center rounded-full bg-gradient-to-br from-[#C2410C] to-[#F59E0B] text-white text-[11px] font-bold"
                          >
                            {counts[n.to] > 9 ? "9+" : counts[n.to]}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User footer — always visible */}
        <div className="shrink-0 px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-amber-300 text-stone-900 font-display text-sm flex items-center justify-center shrink-0">
                {(user?.name || "?")[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-xs text-stone-100 truncate font-medium" data-testid="app-user-name">{user?.name}</p>
              <p className="text-[10px] text-stone-500 truncate">{user?.email}</p>
            </div>
            <button
              type="button"
              data-testid="app-logout"
              onClick={handleLogout}
              title="Sign out"
              className="shrink-0 p-2 rounded-lg text-stone-400 hover:bg-stone-800 hover:text-stone-100 transition-colors"
            >
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 safe-top safe-x z-30 bg-[#FDFBF7]/95 backdrop-blur-sm border-b border-stone-200 px-4 py-3 flex items-center justify-between">
        <WishireLogo size="sm" wordOnly />
        <button
          aria-label="Open menu"
          data-testid="app-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="p-2 rounded-full border border-stone-300"
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </div>

      {open && (
        <div
          className="md:hidden fixed inset-0 bg-black/40 z-30"
          onClick={() => setOpen(false)}
        />
      )}

      <main className="app-main flex-1 min-w-0 pt-14 md:pt-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
